import React, { useEffect, useState } from 'react';
import { Prospect } from '../../shared/types';
import { clinicaProfissionais, clinicaServicos } from '../../shared/data/clinicaData';
import { HeartPulse, Calendar as CalendarIcon, Phone, MapPin, Search, CheckCircle2, ChevronRight, Menu } from 'lucide-react';

interface HealthDemoProps {
  prospect: Prospect;
}

export const HealthDemo: React.FC<HealthDemoProps> = ({ prospect }) => {
  const [selectedDoctor, setSelectedDoctor] = useState(clinicaProfissionais[0].id);
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white text-slate-800 font-sans selection:bg-[var(--ym-prospect-primary)] selection:text-white">
      
      {/* Header Clean */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-slate-100 shadow-sm transition-all">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
             <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[var(--ym-prospect-primary)] to-[var(--ym-prospect-accent)] flex items-center justify-center shadow-lg shadow-[var(--ym-prospect-primary)]/20">
                <HeartPulse className="text-white w-5 h-5" />
             </div>
             <div>
                <h1 className="text-xl font-heading font-black text-slate-900 tracking-tight leading-none">{prospect.trade_name}</h1>
                <span className="text-[10px] text-slate-400 font-bold tracking-widest uppercase">Saúde Avançada</span>
             </div>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-500">
             <a href="#especialidades" className="hover:text-[var(--ym-prospect-primary)] transition-colors">Especialidades</a>
             <a href="#corpo-clinico" className="hover:text-[var(--ym-prospect-primary)] transition-colors">Corpo Clínico</a>
             <a href="#agendamento" className="hover:text-[var(--ym-prospect-primary)] transition-colors">Agendamento</a>
          </nav>
          <div className="flex items-center gap-4">
             <button className="hidden sm:flex bg-[var(--ym-prospect-primary)] text-white px-6 py-2.5 rounded-full font-bold text-sm hover:opacity-90 transition-all shadow-lg shadow-[var(--ym-prospect-primary)]/20 items-center gap-2">
                <CalendarIcon className="w-4 h-4" />
                Agendar Consulta
             </button>
             <button className="md:hidden text-slate-900"><Menu className="w-6 h-6" /></button>
          </div>
        </div>
      </header>

      {/* Hero Humanizado e Asseado */}
      <section className="pt-36 pb-24 px-6 bg-slate-50 relative overflow-hidden">
        {/* Soft UI background blobs */}
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-[var(--ym-prospect-primary)]/5 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/3 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-[var(--ym-prospect-accent)]/5 rounded-full blur-[80px] translate-y-1/3 -translate-x-1/4 pointer-events-none" />
        
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-16 relative z-10">
          <div className="lg:w-1/2 space-y-8">
             <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-100 text-[var(--ym-prospect-primary)] text-xs font-bold shadow-sm uppercase tracking-widest">
                <CheckCircle2 className="w-4 h-4" />
                <span>Atendimento Premium</span>
             </div>
             <h2 className="text-5xl lg:text-7xl font-bold font-heading text-slate-900 tracking-tight leading-[1.1] mb-6">
                Sua saúde merece <br /> a melhor <span className="text-[var(--ym-prospect-primary)]">tecnologia.</span>
             </h2>
             <p className="text-lg text-slate-500 max-w-xl leading-relaxed font-light">
                Agende sua consulta sem ligar, veja seus exames pelo celular e confie no corpo clínico mais respeitado da região.
             </p>
             
             {/* Busca Rápida Estilo Doctoralia */}
             <div className="bg-white p-2 rounded-2xl flex flex-col sm:flex-row gap-2 shadow-xl shadow-slate-200/50 border border-slate-100 max-w-xl mt-8">
                <div className="flex-1 px-4 py-3 flex items-center gap-3 border-b sm:border-b-0 sm:border-r border-slate-100">
                   <Search className="w-5 h-5 text-slate-300" />
                   <input type="text" placeholder="Especialidade ou Médico..." className="bg-transparent border-none outline-none w-full text-sm font-medium text-slate-700 placeholder:text-slate-400" />
                </div>
                <button className="bg-[var(--ym-prospect-primary)] text-white px-8 py-3 rounded-xl text-sm font-bold hover:bg-[var(--ym-prospect-accent)] transition-colors">
                   Buscar
                </button>
             </div>
          </div>
          
          <div className="lg:w-1/2 w-full">
             <div className="relative aspect-[4/3] lg:aspect-[4/4] rounded-[3rem] overflow-hidden shadow-2xl shadow-slate-200">
                <img 
                  src="https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?auto=format&fit=crop&q=80&w=1200" 
                  alt="Clínica Moderna" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent" />
                
                {/* Floating Card Simulating App Integration */}
                <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-md p-4 rounded-2xl shadow-xl flex items-center justify-between">
                   <div className="flex items-center gap-4">
                      <img src={clinicaProfissionais[0].photo} className="w-12 h-12 rounded-full object-cover" alt="Doctor" />
                      <div>
                         <h4 className="font-bold text-sm text-slate-900">{clinicaProfissionais[0].name}</h4>
                         <p className="text-xs text-slate-500">Disponível Hoje</p>
                      </div>
                   </div>
                   <button className="w-10 h-10 rounded-full bg-[var(--ym-prospect-primary)]/10 text-[var(--ym-prospect-primary)] flex items-center justify-center">
                      <ChevronRight className="w-5 h-5" />
                   </button>
                </div>
             </div>
          </div>
        </div>
      </section>

      {/* Serviços / Especialidades (Soft UI Cards) */}
      <section id="especialidades" className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl lg:text-4xl font-black font-heading text-slate-900 mb-4">Nossas Especialidades</h2>
            <p className="text-slate-500 font-light">Procedimentos de alta complexidade em um ambiente totalmente preparado para seu conforto.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
             {clinicaServicos.map(srv => (
                <div key={srv.id} className="bg-slate-50 rounded-3xl p-6 border border-slate-100 hover:shadow-xl hover:shadow-[var(--ym-prospect-primary)]/5 transition-all group">
                   <div className="w-12 h-12 rounded-2xl bg-[var(--ym-prospect-primary)]/10 text-[var(--ym-prospect-primary)] flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                      <HeartPulse className="w-6 h-6" />
                   </div>
                   <h3 className="font-bold text-lg text-slate-900 mb-2">{srv.title}</h3>
                   <p className="text-sm text-slate-500 font-light leading-relaxed">{srv.description}</p>
                </div>
             ))}
          </div>
        </div>
      </section>

      {/* Agendamento Interativo (Calendly-like) */}
      <section id="agendamento" className="py-24 px-6 bg-slate-50 border-y border-slate-100">
         <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-12">
            <div className="lg:w-1/3">
               <h2 className="text-3xl lg:text-4xl font-black font-heading text-slate-900 mb-6">Agende sem complicação.</h2>
               <p className="text-slate-500 font-light mb-8">Escolha o profissional, o melhor dia e horário. Tudo pelo celular ou computador em menos de 2 minutos.</p>
               
               <div className="space-y-4">
                  {clinicaProfissionais.map(doc => (
                     <div 
                        key={doc.id}
                        onClick={() => setSelectedDoctor(doc.id)}
                        className={`p-4 rounded-2xl flex items-center gap-4 cursor-pointer transition-all border ${selectedDoctor === doc.id ? 'bg-white border-[var(--ym-prospect-primary)] shadow-md shadow-[var(--ym-prospect-primary)]/10' : 'bg-transparent border-transparent hover:bg-white/50'}`}
                     >
                        <img src={doc.photo} className="w-14 h-14 rounded-full object-cover" alt={doc.name} />
                        <div>
                           <h4 className="font-bold text-slate-900 text-sm">{doc.name}</h4>
                           <p className="text-xs text-[var(--ym-prospect-primary)] font-medium">{doc.specialty}</p>
                        </div>
                     </div>
                  ))}
               </div>
            </div>

            <div className="lg:w-2/3 bg-white rounded-[3rem] p-8 shadow-xl shadow-slate-200/50 border border-slate-100">
               {/* Simulação de Calendário */}
               <div className="mb-8">
                  <div className="flex items-center justify-between mb-6">
                     <h3 className="font-bold text-lg">Selecione o Dia (Outubro)</h3>
                     <div className="flex gap-2">
                        <button className="w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center text-slate-400 rotate-180"><ChevronRight className="w-4 h-4" /></button>
                        <button className="w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center text-slate-600"><ChevronRight className="w-4 h-4" /></button>
                     </div>
                  </div>
                  <div className="grid grid-cols-7 gap-2">
                     {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(d => (
                        <div key={d} className="text-center text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">{d}</div>
                     ))}
                     {Array.from({length: 31}).map((_, i) => {
                        const day = i + 1;
                        const isAvailable = day % 2 === 0 && day > 10;
                        return (
                           <button 
                              key={i}
                              disabled={!isAvailable}
                              onClick={() => setSelectedDay(day)}
                              className={`aspect-square rounded-2xl flex items-center justify-center text-sm font-bold transition-all ${
                                 selectedDay === day 
                                    ? 'bg-[var(--ym-prospect-primary)] text-white shadow-lg shadow-[var(--ym-prospect-primary)]/30' 
                                    : isAvailable 
                                       ? 'bg-slate-50 text-slate-700 hover:bg-slate-100 cursor-pointer' 
                                       : 'bg-transparent text-slate-300 cursor-not-allowed'
                              }`}
                           >
                              {day}
                           </button>
                        )
                     })}
                  </div>
               </div>

               {selectedDay && (
                  <div className="animate-in slide-in-from-bottom-4 fade-in duration-300">
                     <h3 className="font-bold text-lg mb-4">Horários Disponíveis</h3>
                     <div className="flex flex-wrap gap-3 mb-8">
                        {['09:00', '10:30', '14:00', '15:30', '17:00'].map(time => (
                           <button 
                              key={time}
                              onClick={() => setSelectedTime(time)}
                              className={`px-6 py-3 rounded-xl text-sm font-bold transition-all ${
                                 selectedTime === time 
                                    ? 'bg-[var(--ym-prospect-primary)] text-white shadow-md' 
                                    : 'bg-slate-50 text-slate-600 hover:bg-slate-100 border border-slate-200'
                              }`}
                           >
                              {time}
                           </button>
                        ))}
                     </div>

                     {selectedTime && (
                        <button className="w-full bg-[var(--ym-prospect-primary)] text-white py-4 rounded-2xl font-bold hover:opacity-90 transition-opacity">
                           Confirmar Agendamento — {selectedDay}/10 às {selectedTime}
                        </button>
                     )}
                  </div>
               )}
            </div>
         </div>
      </section>

      {/* Footer Minimalista */}
      <footer className="border-t border-slate-100 bg-white py-12 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <HeartPulse className="text-[var(--ym-prospect-primary)] w-6 h-6" />
            <span className="text-slate-900 font-bold text-lg">{prospect.trade_name}</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-slate-500 font-medium">
            <span className="flex items-center gap-2"><Phone className="w-4 h-4 text-slate-400" /> {prospect.phone}</span>
            <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-slate-400" /> {prospect.city} - {prospect.state}</span>
          </div>
        </div>
      </footer>
    </div>
  );
};
